import { ScrollText } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuditLog, useProjects, useTeams, useUsers, type AuditEntry } from "@/api/queries";
import { useCurrentUser } from "@/app/auth";
import { EmptyState } from "@/components/common/EmptyState";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { errorText } from "@/lib/errors";
import { formatDateTime, shiftDate, zonedToISO } from "@/lib/format";

const areas = [
  "auth",
  "user",
  "team",
  "company",
  "project",
  "task",
  "device",
  "policy",
  "screenshot",
  "slot",
  "app_catalog",
  "manual_time",
  "report",
  "release",
  "system",
  "setup",
] as const;
type Area = (typeof areas)[number];

export function AuditPage() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const [area, setArea] = useState<Area | "">("");
  const [actorId, setActorId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const log = useAuditLog({
    action: area && `${area}.`,
    actorId,
    subjectId,
    from: from ? zonedToISO(from, "00:00", me.timezone) : undefined,
    to: to ? zonedToISO(shiftDate(to, 1), "00:00", me.timezone) : undefined,
  });
  const filtered = Boolean(area || actorId || subjectId || from || to);
  const items = log.data?.pages.flatMap((p) => p.items) ?? [];
  const users = useUsers();
  const teams = useTeams();
  const projects = useProjects();
  const names = new Map<string, string>([
    ...(users.data ?? []).map((u) => [`user:${u.id}`, u.full_name] as const),
    ...(teams.data ?? []).map((team) => [`team:${team.id}`, team.name] as const),
    ...(projects.data ?? []).map((p) => [`project:${p.id}`, p.name] as const),
  ]);
  // Deleted objects: the name recorded in the entry itself.
  const objectName = (e: AuditEntry) => {
    if (e.entity_type === "policy") {
      const scope = e.details.scope;
      if (scope === "company") return t("settings.audit.companyPolicy");
      return typeof scope === "string" ? names.get(`${scope}:${e.entity_id}`) : undefined;
    }
    if (e.entity_type === "screenshot") {
      const owner =
        typeof e.details.user_id === "string" ? names.get(`user:${e.details.user_id}`) : undefined;
      return owner && t("settings.audit.screenshotOf", { name: owner });
    }
    if (e.entity_type === "manual_time") {
      const owner =
        typeof e.details.user_id === "string" ? names.get(`user:${e.details.user_id}`) : undefined;
      return owner && t("settings.audit.manualTimeOf", { name: owner });
    }
    if (e.entity_type === "agent_release" && typeof e.details.version === "string") {
      return t("settings.audit.releaseOf", { version: e.details.version });
    }
    if (e.entity_type === "report_export" && typeof e.details.report === "string") {
      return (t as (k: string) => string)(`reports.tabs.${e.details.report}`);
    }
    if (e.entity_type === "app_catalog") {
      const recorded = e.details.display_name ?? e.details.match_value;
      if (typeof recorded === "string") return recorded;
    }
    const known = names.get(`${e.entity_type}:${e.entity_id}`);
    if (known) return known;
    const recorded = e.details.title ?? e.details.name;
    return typeof recorded === "string" ? recorded : undefined;
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-row flex-wrap items-end justify-between gap-3 pb-4">
        <div>
          <CardTitle>{t("settings.audit.title")}</CardTitle>
          <CardDescription className="max-w-2xl">{t("settings.audit.description")}</CardDescription>
        </div>
      </CardHeader>
      <div className="flex flex-wrap items-center gap-2 px-5 pb-4">
        <NativeSelect
          aria-label={t("settings.audit.area")}
          value={area}
          onChange={(e) => setArea(e.target.value as Area | "")}
        >
          <option value="">{t("settings.audit.allAreas")}</option>
          {areas.map((a) => (
            <option key={a} value={a}>
              {t(`settings.audit.areas.${a}`)}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label={t("settings.audit.actor")}
          value={actorId}
          onChange={(e) => setActorId(e.target.value)}
        >
          <option value="">{t("settings.audit.anyActor")}</option>
          {(users.data ?? [])
            .filter((u) => u.status !== "invited")
            .map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name}
              </option>
            ))}
        </NativeSelect>
        <NativeSelect
          aria-label={t("settings.audit.subject")}
          value={subjectId}
          onChange={(e) => setSubjectId(e.target.value)}
        >
          <option value="">{t("settings.audit.anySubject")}</option>
          {(users.data ?? [])
            .filter((u) => u.status !== "invited")
            .map((u) => (
              <option key={u.id} value={u.id}>
                {t("settings.audit.about", { name: u.full_name })}
              </option>
            ))}
        </NativeSelect>
        <input
          type="date"
          aria-label={t("settings.audit.from")}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="h-9 rounded-lg border bg-surface px-3 font-medium shadow-card"
        />
        <span className="text-muted">—</span>
        <input
          type="date"
          aria-label={t("settings.audit.to")}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="h-9 rounded-lg border bg-surface px-3 font-medium shadow-card"
        />
        {filtered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setArea("");
              setActorId("");
              setSubjectId("");
              setFrom("");
              setTo("");
            }}
          >
            {t("settings.audit.reset")}
          </Button>
        )}
      </div>
      {log.isError ? (
        <div className="px-5 pb-5">
          <Alert variant="danger">{errorText(t, log.error)}</Alert>
        </div>
      ) : !log.isPending && items.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title={filtered ? t("settings.audit.emptyFiltered") : t("settings.audit.empty")}
        />
      ) : (
        <Table className="min-w-[900px]">
          <THead>
            <tr>
              <TH>{t("settings.audit.columns.time")}</TH>
              <TH>{t("settings.audit.columns.actor")}</TH>
              <TH>{t("settings.audit.columns.action")}</TH>
              <TH>{t("settings.audit.columns.details")}</TH>
              <TH>{t("settings.audit.columns.ip")}</TH>
            </tr>
          </THead>
          <tbody>
            {log.isPending
              ? [0, 1, 2, 3].map((i) => (
                  <TR key={i}>
                    <TD colSpan={5}>
                      <Skeleton className="h-6" />
                    </TD>
                  </TR>
                ))
              : items.map((e) => <AuditRow key={e.id} entry={e} object={objectName(e)} />)}
          </tbody>
        </Table>
      )}
      {log.hasNextPage && (
        <div className="flex justify-center border-t p-3">
          <Button
            variant="secondary"
            disabled={log.isFetchingNextPage}
            onClick={() => void log.fetchNextPage()}
          >
            {t("common.loadMore")}
          </Button>
        </div>
      )}
    </Card>
  );
}

function AuditRow({ entry: e, object }: { entry: AuditEntry; object?: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const key = `settings.audit.actions.${e.action}`;
  // Actions come from the server; unknown ones are shown as is.
  const known = i18n.exists(key) ? (t as (k: string) => string)(key) : e.action;
  return (
    <TR>
      <TD className="whitespace-nowrap text-muted tabular-nums">{formatDateTime(e.created_at, lang)}</TD>
      <TD>
        {e.actor ? (
          <div className="flex items-center gap-2">
            <UserAvatar name={e.actor.full_name} className="size-7" />
            <div className="min-w-0">
              <div className="truncate font-medium">{e.actor.full_name}</div>
              <div className="truncate text-xs text-muted">{e.actor.email}</div>
            </div>
          </div>
        ) : (
          <span className="text-muted">{t("settings.audit.system")}</span>
        )}
      </TD>
      <TD>
        <div className="font-medium">{known}</div>
        <div className="text-xs text-muted" title={`${e.entity_type}:${e.entity_id}`}>
          {object ?? <span className="font-mono">{`${e.entity_type}:${e.entity_id.slice(-8)}`}</span>}
        </div>
      </TD>
      <TD className="max-w-md">
        <Details details={e.details} />
      </TD>
      <TD className="font-mono text-xs text-muted">{e.ip ?? "—"}</TD>
    </TR>
  );
}

function Details({ details }: { details: Record<string, unknown> }) {
  const entries = Object.entries(details);
  if (entries.length === 0) return <span className="text-muted">—</span>;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted">{k}</dt>
          <dd className="truncate font-mono" title={format(v)}>
            {format(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function format(v: unknown): string {
  if (v && typeof v === "object" && "from" in v && "to" in v) {
    const c = v as { from: unknown; to: unknown };
    return `${String(c.from)} → ${String(c.to)}`;
  }
  return typeof v === "string" ? v : JSON.stringify(v);
}
